/*
*****************************************************************************
**                                                                         **
** Copyright (c) 2012-2024, Tyker Technology BV                            **
** All rights reserved.                                                    **
**                                                                         **
** This software is the confidential and proprietary information of        **
** Tyker Technology BV. Unauthorized copying or disclosure of this file,   **
** via any medium is strictly prohibited. This file is only available to   **
** authorized individuals with the permission of the copyright holders.    **
** If you encounter this file and do not have permission, please contact   ** 
** the copyright holders and delete this file.                             **
**                                                                         **
***************************************************************************** 
*/

import * as CONST from '../Constants.js';

export default class Task {
    #task
    constructor(task) {
        this.#task = task;
    }

    get taskId() {
        return this.#task.taskId;
    }

    get name() {
        return this.#task.name;
    }

    get description() {
        return this.#task.description;
    }

    get owner_customerId() {
        return this.#task.owner_customerId;
    }

    get created() {
        return this.#task.created;
    }

    get edited() {
        return this.#task.edited;
    }

    get removed() {
        return this.#task.removed;
    }

    get checked() {
        return this.#task.checked;
    }

    get projection() {
        return this.#task.projection;
    }

    get operationPractice() {
        return this.#task.operationPractice;
    }

    get contour_usage_mode() {
        return this.#task.contour_usage_mode || "ALL";
    }

    get status() {
        return this.#task.status;
    }

    get statuses() {
        return this.#task.statuses;
    }

    get project() {
        return this.#task.project;
    }

    get creator_accountId() {
        return this.#task.creator_accountId;
    }

    get statusColor() {
        return Task.getTaskColor(this.#task.status);
    }
    get icon() {
        return Task.getTaskIcon(this.#task.operationPractice);
    }

    isSurvey() {
        if (this.#task.operationPractice == CONST.__OP_SURVEY)
            return true;
        return false;
    }

    getFileId(resourceType, identifierType) {
        if (this.#task.resourcessets.length != 0) {
            var resourcesetIndex = this.#task.resourcessets.findIndex((resourceset) => resourceset.resourceType == resourceType);
            if (resourcesetIndex != -1 && this.#task.resourcessets[resourcesetIndex].layers.length != 0) {
                var layerIndex = this.#task.resourcessets[resourcesetIndex].layers.findIndex((layer) => layer.identifierType == identifierType);
                if (layerIndex != -1 && this.#task.resourcessets[resourcesetIndex].layers[layerIndex].dxffile != null && this.#task.resourcessets[resourcesetIndex].layers[layerIndex].dxffile.length == 1)
                    return this.#task.resourcessets[resourcesetIndex].layers[layerIndex].dxffile[0].dxffileId;
            }
        }

        return null;
    }

    getResourceSet(resourceType) {
        if (this.#task.resourcessets.length != 0) {
            var resourcesetIndex = this.#task.resourcessets.findIndex((resourceset) => resourceset.resourceType == resourceType);
            if (resourcesetIndex != -1 && this.#task.resourcessets[resourcesetIndex].layers.length != 0) {
                return this.#task.resourcessets[resourcesetIndex].layers
            }
        }
        return null;
    }

    hasResourceSet(resourceType) {
        if (this.#task.resourcessets.length != 0) {
            var resourcesetIndex = this.#task.resourcessets.findIndex((resourceset) => resourceset.resourceType == resourceType);
            if (resourcesetIndex != -1 && this.#task.resourcessets[resourcesetIndex].layers.length != 0) {
                return true
            }
        }
        return false;
    }

    // not __IDENTIFIERTYPE_PROPOSEDUPDATE a problem?
    getFiles() {
        var files = [];
        this.#task.resourcessets.forEach(resourcesset => {
            resourcesset.layers.forEach(layer => {
                layer.dxffile.forEach(file => {
                    if (layer.identifierType != '__IDENTIFIERTYPE_PROPOSEDUPDATE') {
                        if (layer.identifierType == '__IDENTIFIERTYPE_MOTIONPLAN') {
                            files.push({
                                name: layer.layerName ? layer.layerName : file.dxffileId ,
                                resourceType: resourcesset.resourceType,
                                identifierType: layer.identifierType,
                                dxffileId: file.dxffileId,
                                dxflayerId: layer.dxflayerId,
                                resourceIcon: Task.getResourceIcon(resourcesset.resourceType),
                                amountFeatures: layer.amountFeatures
                            });
                        } else {
                            files.push({
                                name: file.filename.replace(/^.*[\\\/]/, ''),
                                resourceType: resourcesset.resourceType,
                                identifierType: layer.identifierType,
                                dxffileId: file.dxffileId,
                                dxflayerId: layer.dxflayerId,
                                resourceIcon: Task.getResourceIcon(resourcesset.resourceType),
                                amountFeatures: layer.amountFeatures
                            });
                        }
                    }
                });
            });
        });
        return files;
    }

    getDrawType() {
        switch (this.#task.operationPractice) {
            case CONST.__OP_SPRAYSTEERINGLINES:
                return "lines";
            case CONST.__OP_SPRAYPOINTS:
                return "points";
            default:
                return null;
        }
    }

    static getTaskColor(status) {
        if (status == CONST.__TASKSTATUSTYPE_NEW)
            return "green";
        if (status == CONST.__TASKSTATUSTYPE_DONE)
            return "blue";
        if (status == CONST.__TASKSTATUSTYPE_REMOVED)
            return "red";
        return "grey";
    }

    static getTaskIcon(operationPractice) {
        if (operationPractice == CONST.__OP_SPRAYSTEERINGLINES)
            return "mdi-gesture";
        if (operationPractice == CONST.__OP_MILLINGDEPTHCONTROL)
            return "mdi-arrow-expand-vertical";
        if (operationPractice == CONST.__OP_SURVEY)
            return "mdi-flag-triangle";
        if (operationPractice == CONST.__OP_SPRAYPOINTS)
            return "mdi-vector-polyline";
        if (operationPractice == CONST.__OP_MOTIONPLAN)
            return "mdi-arrow-decision-auto";
        if (operationPractice == "MotionplanSprayPoints")
            return "mdi-vector-polyline";
        if (operationPractice == "MotionplanSprayPointsWithBoundary")
            return "mdi-vector-polyline";
        return "mdi-help";
    }

    static getResourceIcon(resourceType) {
        switch (resourceType) {
            case CONST.__RESOURCETYPE_DXF:
                return "mdi-file-image-marker";
            case CONST.__RESOURCETYPE_CSV:
                return "mdi-file-delimited";
            case CONST.__RESOURCETYPE_MOTIONPLAN:
                return "mdi-file-document";
            default:
                return "mdi-file";
        }
    }
}